import { zodResolver } from '@hookform/resolvers/zod';
import { usePageNavigation } from '@shared/components/navigation/usePageNavigation';
import { useProductsInfinite } from '@shared/hooks/useProductsInfinite';
import { useIsMobile } from '@shared/hooks/useMobile';
import React, { memo, useCallback, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { SelectedProduct } from '@shared/components/ui/ProductSelector';
import { createEntrySchema, CreateEntryFormData } from './models/CreateEntrySchema';
import { EntryDetailsForm, ProductForm, EntryDetailsSkeleton, ProductFormSkeleton } from './components';
import { useSubmitEntry } from './hooks';

const CreateEditView: React.FC = memo(() => {
  const { t } = useTranslation('merchandiseV3');

  // Hook para detectar si estamos en mobile
  const isMobile = useIsMobile();

  // Estado para productos seleccionados
  const [selectedProducts, setSelectedProducts] = useState<SelectedProduct[]>([]);

  // Configurar react-hook-form con Zod
  const form = useForm<CreateEntryFormData>({
    resolver: zodResolver(createEntrySchema),
    mode: 'onChange',
    defaultValues: {
      estimated_delivery_at: '',
      boxes_count: null,
      tracking_code: null,
      products: [],
    },
  });

  const {
    handleSubmit,
    setValue,
    trigger,
    formState: { errors, isValid },
  } = form;

  // Cargar productos desde la API
  const { products: apiProducts, isLoading: isLoadingProducts } = useProductsInfinite({
    limit: 1000,
    enabled: true,
    fields: 'basic',
  });

  // Hook personalizado para manejar el submit
  const { onSubmit, onCancel, isLoading } = useSubmitEntry({
    form,
    selectedProducts,
    onResetSelectedProducts: useCallback(() => setSelectedProducts([]), []),
  });

  // Convertir productos - SOLO cuando apiProducts cambie REALMENTE
  const availableProducts = useMemo(() => {
    if (!apiProducts?.length) return [];

    return apiProducts.map((product) => ({
      id: product.id,
      name: product.name,
      code: product.code || '',
      image: product.image_hash || product.image || '',
    }));
  }, [apiProducts]);

  // Handler para cuando cambian los productos seleccionados
  const handleProductsChange = useCallback(
    (products: SelectedProduct[]) => {
      setSelectedProducts(products);
      setValue('products', products);
      trigger('products');
    },
    [setValue, trigger],
  );

  // Breadcrumbs - dinámicos según dispositivo
  const breadcrumbs = useMemo(() => {
    if (isMobile) {
      // Mobile: Solo mostrar "Crear"
      return [{ label: t('create.breadcrumb', 'Crear entrada') }];
    }

    // Desktop: Breadcrumbs completos
    return [
      { label: t('breadcrumb.merchandise', 'Mercancía'), path: '/merchandise' },
      { label: t('create.breadcrumb', 'Crear entrada') },
    ];
  }, [t, isMobile]);

  // Acciones del header - dinámicas según dispositivo
  const headerActions = useMemo(() => {
    if (isMobile) {
      // Mobile: Solo botón de crear
      return [
        {
          id: 'create',
          label: isLoading ? t('create.creating', 'Creando...') : t('create.button', 'Crear entrada'),
          onClick: handleSubmit(onSubmit),
          variant: 'primary' as const,
          disabled: isLoading || !isValid,
        },
      ];
    }

    // Desktop: Ambos botones
    return [
      {
        id: 'cancel',
        label: t('create.cancel', 'Cancelar'),
        onClick: onCancel,
        variant: 'outline' as const,
      },
      {
        id: 'create',
        label: isLoading ? t('create.creating', 'Creando...') : t('create.button', 'Crear entrada'),
        onClick: handleSubmit(onSubmit),
        variant: 'primary' as const,
        disabled: isLoading || !isValid,
      },
    ];
  }, [onCancel, isLoading, isValid, handleSubmit, onSubmit, t, isMobile]);

  // Configurar breadcrumbs y acciones del header
  usePageNavigation({ breadcrumbs, headerActions });

  return (
    <div className="px-6 pt-4 space-y-8" data-testid="entry-create-view">
      {/* Formulario con FormProvider */}
      {isLoadingProducts ? (
        <>
          <EntryDetailsSkeleton />
          <ProductFormSkeleton />
        </>
      ) : (
        <FormProvider {...form}>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
            {/* Sección 1: Información básica */}
            <EntryDetailsForm
              isLoading={isLoading}
              disabled={isLoading}
              title={t('form.details.title', 'Información de la entrada')}
              description={t('form.details.description', 'Completa los datos básicos de la entrada')}
            />

            {/* Sección 2: Selección de productos */}
            <ProductForm
              products={availableProducts}
              initialSelectedProducts={selectedProducts}
              onProductsChange={handleProductsChange}
              isLoading={isLoadingProducts}
              disabled={isLoading}
              allowMultiple={true}
              maxQuantity={999999999}
              title={t('form.products.title', 'Productos')}
              description={t('form.products.description', 'Selecciona los productos de la entrada')}
            />

            {/* Error de productos */}
            {errors.products && (
              <div className="grid grid-cols-1 lg:grid-cols-6 gap-8">
                <div className="lg:col-span-2"></div>
                <div className="lg:col-span-4">
                  <p className="text-sm text-destructive" data-testid="products-error">
                    {errors.products.message}
                  </p>
                </div>
              </div>
            )}
          </form>
        </FormProvider>
      )}
    </div>
  );
});

CreateEditView.displayName = 'CreateEditView';

export default CreateEditView;
